/**
 * Shared flattening of a return's shipping / billing contact into the
 * inline `ship_*` / `bill_*` fields the RMA endpoints expect.
 *
 * Every return form (create, process, quick-ship, invoice) serialises the
 * chosen shipping or billing contact into the same flat set of address
 * fields before POSTing. This util owns that single mapping so the four
 * forms can't drift on which fields go out or how the company name is
 * resolved. Each form keeps ownership of its own id field, because the
 * endpoints disagree on the key name (`shipping_contact_id` on create vs
 * `shipping_contact` on process/quick-ship) and on when it is emitted.
 *
 * Contains:
 * - isValidContactId — true when an id looks like a saved Contact PK
 * - shipContactFields — the flat ship_* fields for a shipping contact
 * - billContactFields — the flat bill_* fields for a billing contact
 */

/** Returns true when id looks like a saved Contact record primary key. */
export const isValidContactId = id => typeof id === 'number' || (typeof id === 'string' && /^\d+$/.test(id));

/**
 * Resolve the company name stamped onto an inline contact: the site group's
 * name wins, then the customer's, then the contact's own business name.
 */
function resolveCompanyName(contact, siteGroupName, customerName) {
  return siteGroupName || customerName || contact.business_name || '';
}

/**
 * Flatten a shipping contact into the inline `ship_*` payload fields.
 *
 * Does NOT include the contact id — callers set their own id key
 * (`shipping_contact_id` vs `shipping_contact`) with their own condition.
 *
 * @param {object} contact - the selected shipping contact (plain object)
 * @param {{ siteGroupName?: string, customerName?: string }} [names]
 * @returns {object} the ship_* fields to spread into a payload
 */
export function shipContactFields(contact, { siteGroupName = '', customerName = '' } = {}) {
  return {
    ship_contact: contact.name || '',
    ship_company_name: resolveCompanyName(contact, siteGroupName, customerName),
    ship_email: contact.email || '',
    ship_phone: contact.phone1 || '',
    ship_address: contact.address || '',
    ship_city: contact.city || '',
    ship_state: contact.state || '',
    ship_zip: contact.zipcode || '',
    ship_country: contact.country || '',
  };
}

/**
 * Flatten a billing contact into the inline `bill_*` payload fields.
 *
 * Does NOT include the contact id — callers set their own id key
 * (`billing_contact_id` vs `billing_contact`) with their own condition.
 *
 * @param {object} contact - the selected billing contact (plain object)
 * @param {{ siteGroupName?: string, customerName?: string }} [names]
 * @returns {object} the bill_* fields to spread into a payload
 */
export function billContactFields(contact, { siteGroupName = '', customerName = '' } = {}) {
  return {
    bill_contact: contact.name || '',
    bill_company_name: resolveCompanyName(contact, siteGroupName, customerName),
    bill_email: contact.email || '',
    bill_phone: contact.phone1 || '',
    bill_address: contact.address || '',
    bill_city: contact.city || '',
    bill_state: contact.state || '',
    bill_zip: contact.zipcode || '',
    bill_country: contact.country || '',
  };
}
